import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchCollabQuizHistory,
} from "../../vsecurite/vsecuriteSlice";
import moment from "moment";
import "moment/locale/fr";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import Langs, { Localize } from "../../../app/lang/langs";

/* ── Palette (aligned with the rest of the quiz module) ── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const GREEN = "#16a34a";
const GREEN_LIGHT = "rgba(22,163,74,0.08)";
const RED = "#dc2626";
const RED_LIGHT = "rgba(220,38,38,0.07)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

/* Set moment to French locale globally for this module */
moment.locale("fr");

/* ── Fade-in keyframe injected once ── */
const KEYFRAMES = `
    @keyframes qhFadeInUp {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

/**
 * Groups an array of attempt records by their quiz title.
 */
function groupByQuiz(history) {
    const map = new Map();

    history.forEach((attempt) => {
        const key = attempt.id_quiz;
        if (!map.has(key)) {
            map.set(key, {
                id_quiz: attempt.id_quiz,
                quiz_title: attempt.quiz_title,
                anyPassed: false,
                allowCorrections: Boolean(attempt.allow_corrections),
                allowScores: Boolean(attempt.allow_scores),
                attempts: [],
            });
        }
        const group = map.get(key);
        group.attempts.push(attempt);
        if (attempt.passed) group.anyPassed = true;
    });

    map.forEach((group) => {
        group.attempts.sort((a, b) => b.attempt_number - a.attempt_number);
    });

    return Array.from(map.values()).sort((a, b) => {
        const latestA = a.attempts[0]?.started_at || "";
        const latestB = b.attempts[0]?.started_at || "";
        return latestB.localeCompare(latestA);
    });
}

/**
 * QuizHistory – Shows a collab's full quiz attempt history grouped by quiz.
 */
export default function QuizHistory({ onReviewQuiz }) {
    const dispatch = useDispatch();

    const collabHistory = useSelector((state) => state.vsecurite.quizzes.collabHistory);
    const loading = useSelector((state) => state.vsecurite.quizzes.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);

    const theme = useTheme();
    const isSmallQuery = useMediaQuery(theme.breakpoints.down("sm"));
    const isSmall = isMobileRedux === 2 || isSmallQuery;

    useEffect(() => {
        dispatch(fetchCollabQuizHistory());
    }, [dispatch]);

    const groups = useMemo(() => groupByQuiz(collabHistory || []), [collabHistory]);

    /* ── Loading state ── */
    if (loading && collabHistory.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: ORANGE }} />
            </Box>
        );
    }

    return (
        <>
            <style>{KEYFRAMES}</style>
            <Box
                sx={{
                    maxHeight: isSmall ? "none" : "calc(100vh - 280px)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: isSmall ? "visible" : "hidden",
                    mt: 0,
                }}
            >
                {/* Section header */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: isSmall ? 2 : 3, flexShrink: 0 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "11px",
                            background: ORANGE_LIGHT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <HistoryRoundedIcon sx={{ fontSize: 20, color: ORANGE }} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: isSmall ? "1rem" : "1.1rem",
                            fontWeight: 650,
                            color: TEXT_PRIMARY,
                            letterSpacing: "-0.02em",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                        }}
                    >
                        <Langs str="VSEC_QUIZ_HISTORY_TITLE" />
                    </Typography>
                </Stack>

                {/* Empty state */}
                {groups.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            py: isSmall ? 5 : 8,
                            gap: 1.5,
                            animation: `qhFadeInUp 500ms ${EASE} both`,
                        }}
                    >
                        <HistoryRoundedIcon sx={{ fontSize: 42, color: TEXT_MUTED, mb: 1 }} />
                        <Typography
                            sx={{
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: TEXT_PRIMARY,
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                textAlign: "center",
                            }}
                        >
                            <Langs str="VSEC_QUIZ_HISTORY_EMPTY_TITLE" />
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.86rem",
                                color: TEXT_MUTED,
                                fontFamily: "'Inter',sans-serif",
                                textAlign: "center",
                                px: isSmall ? 2 : 0,
                            }}
                        >
                            <Langs str="VSEC_QUIZ_HISTORY_EMPTY_SUB" />
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            overflowY: isSmall ? "visible" : "auto",
                            flex: isSmall ? "none" : 1,
                            minHeight: 0,
                            WebkitOverflowScrolling: "touch",
                            "&::-webkit-scrollbar": { width: "5px" },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": { background: "rgba(255,144,21,0.20)", borderRadius: "3px" },
                            "&::-webkit-scrollbar-thumb:hover": { background: "rgba(255,144,21,0.35)" },
                        }}
                    >
                        {groups.map((group, idx) => (
                            <QuizHistoryGroup
                                key={group.id_quiz}
                                group={group}
                                animationDelay={idx * 60}
                                onReviewQuiz={onReviewQuiz}
                                isSmall={isSmall}
                                lang={lang}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </>
    );
}

/**
 * QuizHistoryGroup – An accordion for a single quiz, listing all its attempts.
 */
function QuizHistoryGroup({ group, animationDelay, onReviewQuiz, isSmall, lang }) {
    const { id_quiz, quiz_title, anyPassed, allowCorrections, allowScores, attempts } = group;

    return (
        <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={false}
            sx={{
                borderRadius: isSmall ? "12px !important" : "16px !important",
                border: `1px solid ${allowScores ? (anyPassed ? "rgba(22,163,74,0.18)" : "rgba(220,38,38,0.15)") : BORDER_SUBTLE}`,
                background: "#ffffff",
                animation: `qhFadeInUp 500ms ${EASE} ${animationDelay}ms both`,
                transition: `box-shadow 300ms ${EASE}, border-color 300ms ${EASE}`,
                "&::before": { display: "none" },
                "&:hover": isSmall ? {} : {
                    boxShadow: "0 3px 16px rgba(0,0,0,0.06)",
                },
                "&.Mui-expanded": {
                    margin: 0,
                    borderRadius: isSmall ? "12px !important" : "16px !important",
                },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: TEXT_MUTED }} />}
                sx={{
                    px: isSmall ? 1.5 : 2.5,
                    minHeight: isSmall ? 56 : 64,
                    "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        gap: isSmall ? 1 : 1.5,
                        my: 1.5,
                    },
                }}
            >
                {/* Status icon */}
                {allowScores ? (
                    anyPassed
                        ? <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 22, flexShrink: 0 }} />
                        : <CancelRoundedIcon sx={{ color: RED, fontSize: 22, flexShrink: 0 }} />
                ) : (
                    <ScheduleRoundedIcon sx={{ color: TEXT_MUTED, fontSize: 22, flexShrink: 0 }} />
                )}

                {/* Quiz title */}
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: isSmall ? "0.88rem" : "0.95rem",
                        fontWeight: 620,
                        color: TEXT_PRIMARY,
                        letterSpacing: "-0.01em",
                        fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: isSmall ? "normal" : "nowrap",
                        lineHeight: isSmall ? 1.3 : undefined,
                    }}
                >
                    {quiz_title}
                </Typography>

                {/* Overall status chip */}
                {allowScores && (
                    <Chip
                        label={anyPassed ? Localize("VSEC_CHIP_PASSED_QUIZ", lang) : Localize("VSEC_CHIP_FAILED_QUIZ", lang)}
                        size="small"
                        sx={{
                            fontSize: isSmall ? "0.7rem" : "0.74rem",
                            fontWeight: 600,
                            fontFamily: "'Inter',sans-serif",
                            color: anyPassed ? GREEN : RED,
                            background: anyPassed ? GREEN_LIGHT : RED_LIGHT,
                            border: `1px solid ${anyPassed ? "rgba(22,163,74,0.2)" : "rgba(220,38,38,0.15)"}`,
                            borderRadius: "8px",
                            height: 24,
                            flexShrink: 0,
                            "& .MuiChip-label": { px: isSmall ? 0.8 : 1.2 },
                        }}
                    />
                )}
            </AccordionSummary>

            <AccordionDetails sx={{ px: isSmall ? 1.5 : 2.5, pb: 2.5, pt: 0 }}>
                <Divider sx={{ mb: 2, borderColor: BORDER_SUBTLE }} />

                {/* Attempt list */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {attempts.map((attempt) => (
                        <AttemptRow key={attempt.id} attempt={attempt} allowScores={allowScores} isSmall={isSmall} lang={lang} />
                    ))}
                </Box>

                {/* Review button */}
                {allowCorrections ? (
                    <Box sx={{ mt: 2.5 }}>
                        <Button
                            variant="outlined"
                            onClick={() => onReviewQuiz(id_quiz)}
                            fullWidth={isSmall}
                            startIcon={<RateReviewOutlinedIcon sx={{ fontSize: "17px !important" }} />}
                            sx={{
                                borderRadius: "11px",
                                px: 2.5,
                                py: 0.8,
                                minHeight: isSmall ? 48 : "auto",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.86rem",
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                color: ORANGE,
                                borderColor: "rgba(255,144,21,0.35)",
                                transition: `all 250ms ${EASE}`,
                                "&:hover": {
                                    borderColor: ORANGE,
                                    background: ORANGE_LIGHT,
                                    transform: isSmall ? "none" : "translateY(-1px)",
                                },
                                "&:active": { transform: "scale(0.98)" },
                            }}
                        >
                            {Localize("VSEC_BTN_REVIEW_ANSWERS", lang)}
                        </Button>
                    </Box>
                ) : null}
            </AccordionDetails>
        </Accordion>
    );
}

/**
 * AttemptRow – Displays one quiz attempt: number, date, score, pass/fail chip.
 */
function AttemptRow({ attempt, allowScores, isSmall, lang }) {
    const { attempt_number, started_at, score, passed } = attempt;

    const dateLabel = started_at
        ? moment(started_at).format("D MMM YYYY")
        : "—";

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={isSmall ? 1 : 1.5}
            sx={{
                px: 1.5,
                py: isSmall ? 1.2 : 1,
                borderRadius: "10px",
                background: allowScores ? (passed ? GREEN_LIGHT : RED_LIGHT) : "rgba(0,0,0,0.025)",
                border: `1px solid ${allowScores ? (passed ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.10)") : "rgba(0,0,0,0.05)"}`,
                transition: `all 200ms ${EASE}`,
                minHeight: isSmall ? 44 : "auto",
            }}
        >
            {/* Attempt number badge */}
            <Box
                sx={{
                    minWidth: 26,
                    height: 26,
                    borderRadius: "7px",
                    background: allowScores ? (passed ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.08)") : "rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: allowScores ? (passed ? GREEN : RED) : TEXT_MUTED,
                        fontFamily: "'Inter',sans-serif",
                    }}
                >
                    #{attempt_number}
                </Typography>
            </Box>

            {/* Date */}
            <Typography
                sx={{
                    fontSize: isSmall ? "0.8rem" : "0.82rem",
                    color: TEXT_SECONDARY,
                    fontFamily: "'Inter',sans-serif",
                    flex: 1,
                }}
            >
                {dateLabel}
            </Typography>

            {/* Score */}
            {allowScores && (
                <Typography
                    sx={{
                        fontSize: "0.88rem",
                        fontWeight: 650,
                        color: passed ? GREEN : RED,
                        fontFamily: "'Inter',sans-serif",
                        minWidth: 44,
                        textAlign: "right",
                    }}
                >
                    {score}%
                </Typography>
            )}

            {/* Pass / Fail chip */}
            {allowScores && (
                <Chip
                    label={passed ? Localize("VSEC_CHIP_PASSED_ATTEMPT", lang) : Localize("VSEC_CHIP_FAILED_ATTEMPT", lang)}
                    size="small"
                    sx={{
                        fontSize: isSmall ? "0.7rem" : "0.72rem",
                        fontWeight: 600,
                        fontFamily: "'Inter',sans-serif",
                        color: passed ? GREEN : RED,
                        background: passed ? GREEN_LIGHT : RED_LIGHT,
                        border: `1px solid ${passed ? "rgba(22,163,74,0.18)" : "rgba(220,38,38,0.15)"}`,
                        borderRadius: "7px",
                        height: 22,
                        "& .MuiChip-label": { px: isSmall ? 0.8 : 1 },
                    }}
                />
            )}
        </Stack>
    );
}
